import {
  alpha,
  Alert,
  Box,
  ButtonProps,
  keyframes,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowForward,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { isUndefined } from 'lodash';
import {
  MouseEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ActionProps,
  LinkAwareButton,
} from '../link-aware-button/link-aware-button';
import Confetti from './confetti-component';

/**
 * The time-left bar along the toast's bottom edge: full width at the start,
 * gone when the toast dismisses itself. A CSS animation rather than a ticking
 * state, so it costs no renders — and `animation-play-state` pauses it on
 * hover in step with the dismiss timer.
 */
const timeLeft = keyframes`
  from { transform: scaleX(1); }
  to { transform: scaleX(0); }
`;

// Animation keyframes
const slideInBounce = keyframes`
  0% {
    transform: translateX(100%) scale(0.8);
    opacity: 0;
  }
  60% {
    transform: translateX(-10px) scale(1.05);
    opacity: 0.9;
  }
  80% {
    transform: translateX(5px) scale(0.98);
    opacity: 1;
  }
  100% {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
`;

const slideOutFade = keyframes`
  0% {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
  100% {
    transform: translateX(100%) scale(0.8);
    opacity: 0;
  }
`;

// Mobile equivalents of the pair above. A full-width toast pinned to the bottom
// edge has nowhere to slide in from horizontally — it already spans the screen —
// so it rises off the edge instead, with the same overshoot-and-settle curve.
// The scale is dropped: scaling a full-width element in from 0.8 makes it
// visibly narrower than the screen mid-animation, which reads as a glitch.
const slideUpBounce = keyframes`
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  60% {
    transform: translateY(-8px);
    opacity: 0.9;
  }
  80% {
    transform: translateY(4px);
    opacity: 1;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideDownFade = keyframes`
  0% {
    transform: translateY(0);
    opacity: 1;
  }
  100% {
    transform: translateY(100%);
    opacity: 0;
  }
`;

export interface Toast {
  id?: string | number;
  title: string;
  body: ReactNode;
  // @deprecated use severity instead
  bg?: string;
  severity?: 'success' | 'error' | 'warning' | 'info';
  /**
   * Replaces the severity's default icon — a calendar for a booking, a
   * truck for a pickup. It's tinted with the severity colour either way.
   */
  icon?: ReactNode;
  /**
   * The words to time the toast by when `body` isn't plain text (e.g. a
   * ToastBody) — see `toastBodyText`.
   */
  readingText?: string;
  delay?: number;
  autohide?: boolean;
  /**
   * A button along the bottom of the toast. When it goes somewhere, give it an
   * `href` (an in-app path or an absolute URL) rather than an `onClick` that
   * navigates: it then renders as a real link, so cmd/ctrl/middle-click opens
   * it in a new tab.
   */
  action?: ActionProps;
  showConfetti?: boolean; // Flag to enable confetti animation for this specific toast
  removeOnNextPush?: boolean; // Flag to remove existing toast when a new toast is pushed, this is useful for toasts that represent transient states OR when if the generic network error toast should be superseded by a more specific one
}

/**
 * How long a toast with no `delay` stays up: long enough to read it. A
 * flat 3s was fine for "Saved" but cut off toasts that name the record and
 * say what's next — ~4 words a second, 4s floor, 12s cap, and at least 6s
 * when there's a button to reach for.
 */
export function defaultDismissMs(
  toast: Pick<Toast, 'title' | 'body' | 'action' | 'readingText'>,
) {
  const text = `${toast.title ?? ''} ${
    toast.readingText ?? (typeof toast.body === 'string' ? toast.body : '')
  }`;
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const reading = 2000 + words * 250;
  const floor = toast.action ? 6000 : 4000;
  return Math.min(12000, Math.max(floor, reading));
}

/**
 * Whether a link would only take you to the page you're on — then the
 * button is noise. Query strings are ignored, and a link to a parent of the
 * current path (the job, from one of its tabs) counts as here. A link to a
 * child path does not: from a bare parent it goes to a specific tab or
 * sub-page, which is a real call to action.
 */
export function linksToCurrentPage(
  href: string | undefined,
  pathname: string = typeof window === 'undefined'
    ? ''
    : window.location.pathname,
): boolean {
  if (!href || !href.startsWith('/') || !pathname) return false;
  const trim = (p: string) => p.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  const target = trim(href);
  const here = trim(pathname);
  return target === here || here.startsWith(`${target}/`);
}

function isModifiedClick(e: MouseEvent) {
  return e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
}

/**
 * The toast's action. With an `href` it's an anchor — an in-app path routes
 * through react-router (no reload), anything else is a plain link — and a
 * plain click dismisses the toast; a modified click (new tab) leaves it up.
 */
function ToastActionButton({
  action,
  onFollowLink,
  color,
  sx,
}: {
  action: NonNullable<Toast['action']>;
  onFollowLink: () => void;
  color: ButtonProps['color'];
  sx?: ButtonProps['sx'];
}) {
  const { label, href, onClick, ...buttonProps } = action;

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    if (href && !isModifiedClick(e)) onFollowLink();
  };

  return (
    <LinkAwareButton
      // A footer row across the whole toast: a divider, the label on the
      // left and — for a link — an arrow on the right. It keeps the action
      // apart from the message and its checklist, and the full row is the
      // tap target on a phone.
      variant="text"
      color={color}
      fullWidth
      endIcon={href ? <ArrowForward /> : undefined}
      {...buttonProps}
      sx={[
        sx,
        ...(Array.isArray(buttonProps.sx) ? buttonProps.sx : [buttonProps.sx]),
      ]}
      href={href}
      onClick={handleClick}
    >
      {label}
    </LinkAwareButton>
  );
}

export default function ToastrRenderer({
  toast,
  onRemove = () => {},
}: {
  toast: Toast;
  onRemove?: (id: string | number) => void;
}) {
  const [open, setOpen] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const alertRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const theme = useTheme();
  // Drives both the geometry (full-width vs. a 400px card) and the direction
  // the toast animates from, which have to agree — see the container's own
  // breakpoint switch in toastr-context.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const remainingTimeRef = useRef<number | null>(null);

  const severity = useMemo(() => {
    if (toast.severity) {
      return toast.severity;
    }
    // Fallback to bg for backwards compatibility
    // 'primary' and 'error' aren't Bootstrap variants, but callers use them —
    // 'primary' for confirmations, 'error' for failures — and they used to
    // fall through to info (blue).
    switch (toast.bg) {
      case 'success':
      case 'primary':
        return 'success';
      case 'danger':
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
      default:
        return 'info';
    }
  }, [toast.bg, toast.severity]);

  const icon = useMemo(() => {
    if (toast.icon) return toast.icon;
    switch (severity) {
      case 'success':
        return <CheckIcon />;
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
      default:
        return <InfoIcon />;
    }
  }, [severity, toast.icon]);

  const isDark = theme.palette.mode === 'dark';
  // The lighter shade on a dark card: some `main`s (success green) are too
  // dark to read there.
  const accent = isDark
    ? theme.palette[severity].light
    : theme.palette[severity].main;
  const tint = alpha(accent, isDark ? 0.12 : 0.08);
  // Light, but readable: the accent itself, except amber, which needs its
  // darker shade to read as text on its own tint.
  const actionColor =
    !isDark && severity === 'warning' ? theme.palette.warning.dark : accent;
  const autoDismissMs = !isUndefined(toast.delay)
    ? toast.delay
    : defaultDismissMs(toast);

  const handleClose = () => {
    setOpen(false);
    setShowConfetti(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (toast.id) {
      setTimeout(() => onRemove(toast.id!), 500); // Increased delay for better animation
    }
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      // Calculate remaining time
      if (startTimeRef.current !== null) {
        const elapsed = Date.now() - startTimeRef.current;
        const totalDelay = autoDismissMs;
        remainingTimeRef.current = Math.max(0, totalDelay - elapsed);
      }
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    // Restart timer with remaining time
    if (remainingTimeRef.current !== null && remainingTimeRef.current > 0) {
      startTimeRef.current = Date.now();
      timeoutRef.current = setTimeout(() => {
        setOpen(false);
        setShowConfetti(false);
        if (toast.id) {
          setTimeout(() => onRemove(toast.id!), 500);
        }
      }, remainingTimeRef.current);
    }
  };

  // Trigger confetti only for toasts that explicitly request it
  useEffect(() => {
    if (open && toast.showConfetti) {
      setShowConfetti(true);
      // Stop confetti after animation duration
      const confettiTimer = setTimeout(() => {
        setShowConfetti(false);
      }, 3000);
      return () => clearTimeout(confettiTimer);
    }
    return undefined;
  }, [severity, open, toast.showConfetti, toast.id]);

  // Auto-hide functionality with hover pause support
  useEffect(() => {
    if (open && autoDismissMs > 0) {
      const delay = autoDismissMs;
      startTimeRef.current = Date.now();
      remainingTimeRef.current = delay;

      timeoutRef.current = setTimeout(() => {
        setOpen(false);
        setShowConfetti(false);
        if (toast.id) {
          setTimeout(() => onRemove(toast.id!), 500);
        }
      }, delay);

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }
    return undefined;
  }, [open, autoDismissMs, toast.id, onRemove]);

  return (
    <>
      <Confetti
        show={showConfetti && toast.showConfetti}
        anchorRef={alertRef}
      />

      <Alert
        ref={alertRef}
        onClose={handleClose}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        severity={severity}
        icon={icon}
        variant="standard"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          // On a phone the container already bounds the width to the screen
          // less its gutters, so the toast just fills it. The 300px floor has
          // to go with it: on a 320px screen that plus the gutters overflows.
          maxWidth: isMobile ? 'none' : '400px',
          minWidth: isMobile ? 0 : '300px',
          // A light card, not a filled colour block: severity shows in the
          // icon, the bottom bar and a faint wash of its colour, so the text
          // stays easy to read. The wash is layered over the paper colour
          // (not a translucent background) so the card stays opaque over the
          // page, in light and dark mode alike.
          bgcolor: 'background.paper',
          backgroundImage: `linear-gradient(${tint}, ${tint})`,
          color: 'text.primary',
          borderRadius: 2,
          px: 2,
          pt: 1.5,
          pb: 2,
          boxShadow: open
            ? '0 8px 28px rgba(0,0,0,0.14)'
            : '0 4px 16px rgba(0,0,0,0.08)',
          animation: open
            ? `${isMobile ? slideUpBounce : slideInBounce} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)`
            : `${isMobile ? slideDownFade : slideOutFade} 0.3s ease-out forwards`,
          transformOrigin: isMobile ? 'center bottom' : 'center right',
          '& .MuiAlert-icon': {
            color: accent,
            opacity: 1,
            mr: 1.5,
            py: 0.25,
          },
          '& .MuiAlert-message': {
            width: '100%',
            // MUI clips this (overflow: auto); the action's footer row
            // reaches past it to the card's edges.
            overflow: 'visible',
            py: 0.25,
          },
          // The close button floats in the corner rather than taking a
          // column, so the body and action get the card's full width.
          '& .MuiAlert-action': {
            position: 'absolute',
            top: theme.spacing(0.75),
            right: theme.spacing(0.75),
            m: 0,
            p: 0,
            color: 'text.secondary',
          },
          // Add a subtle scale effect on hover. Desktop only: a full-width toast
          // scaled to 1.02 pushes past both screen edges, and a phone has no
          // hover to begin with — the pointer state a touch leaves behind would
          // just stick the toast at 1.02 until it dismissed itself.
          ...(isMobile
            ? {}
            : {
                '&:hover': {
                  transform: 'scale(1.02)',
                  transition: 'transform 0.2s ease-out',
                },
              }),
          ...(isHovered && {
            boxShadow: '0 12px 36px rgba(0,0,0,0.18)',
          }),
        }}
      >
        <Typography
          component="div"
          variant="overline"
          // An overline in the severity colour labels the toast; the body
          // underneath carries the message. Clear of the floating close
          // button — only the title row needs it.
          sx={{
            display: 'block',
            color: accent,
            fontWeight: 700,
            letterSpacing: '0.06em',
            lineHeight: 1.5,
            pr: 3.5,
          }}
        >
          {toast.title}
        </Typography>

        {toast.body && (
          <Typography
            component="div"
            variant="body2"
            sx={{ color: 'text.primary', mt: 0.25, lineHeight: 1.5 }}
          >
            {toast.body}
          </Typography>
        )}

        {toast.action && !linksToCurrentPage(toast.action.href) && (
          <ToastActionButton
            action={toast.action}
            onFollowLink={handleClose}
            color={severity}
            sx={{
              // Out to the card's edges: back past the icon column and the
              // side padding, and down to the time-left bar.
              mt: 1.5,
              mb: -2,
              ml: `calc(-1 * (${theme.spacing(2)} + 28px + ${theme.spacing(1.5)}))`,
              mr: -2,
              width: `calc(100% + ${theme.spacing(2)} + 28px + ${theme.spacing(1.5)} + ${theme.spacing(2)})`,
              px: 2,
              pt: 1.25,
              pb: 1.75,
              justifyContent: 'center',
              borderRadius: 0,
              borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.07)}`,
              fontWeight: 400,
              fontSize: '0.8125rem',
              textTransform: 'none',
              // The darker shade keeps amber (and every severity) readable
              // as text on its tint.
              color: actionColor,
              '& .MuiButton-endIcon': {
                ml: 0.75,
                '& svg': { fontSize: '0.9rem' },
              },
              '&:hover': { bgcolor: alpha(actionColor, 0.06) },
            }}
          />
        )}

        {/* Time left: shrinks to nothing as the toast counts down, paused
            while hovered. A toast that doesn't dismiss itself keeps a full,
            still bar. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 4,
            bgcolor: accent,
            transformOrigin: 'left center',
            ...(autoDismissMs > 0 && {
              animation: `${timeLeft} ${autoDismissMs}ms linear forwards`,
              animationPlayState: isHovered ? 'paused' : 'running',
            }),
          }}
        />
      </Alert>
    </>
  );
}
